import {
    Component,
    h,
    Prop,
    Host,
    ComponentInterface,
    Event,
    EventEmitter,
    Watch,
} from "@stencil/core";

@Component({
    tag: "uic-search-field",
    styleUrl: "search-field.scss",
})
export class SearchField implements ComponentInterface {
    private inputElement: HTMLInputElement;

    /**
     * If set to `true`, the user cannot enter text input.
     */
    @Prop() disabled = false;

    /**
     * The placeholder text.
     */
    @Prop() placeholder?: string;

    /**
     * The value of the input.
     */
    @Prop({ mutable: true }) value?: string = "";

    /**
     * Fired whenever the value of the input changes.
     * This event combines `input` and `change` events.
     */
    @Event({ eventName: "uicChange" }) change: EventEmitter<string>;

    @Watch("value")
    handleValueChanged() {
        this.change.emit(this.value);
    }

    private setFocus() {
        // styleguide requirements for touch interaction
        // require minimum touch areas that delegate focus

        if (this.inputElement !== document.activeElement) {
            this.inputElement.focus();
        }
    }

    private handleInputChange(event: any) {
        const target = event.target as HTMLInputElement;
        if (target) {
            this.value = target.value;
        }
    }

    private handleClearInput() {
        this.value = "";
    }

    private renderSearchIcon() {
        return (
            <svg
                class="uic-search-field__icon"
                viewBox="0 0 32 32"
                role="img"
                fill="currentColor"
            >
                <path d="M12.8 3.872c-3.2 0.576-6.080 2.848-7.488 5.888-1.056 2.304-1.184 5.12-0.352 7.616 1.024 3.104 3.904 5.792 7.168 6.624 1.6 0.416 3.776 0.416 5.28-0.032 1.056-0.32 2.496-0.992 3.040-1.376 0.224-0.192 0.384-0.064 3.232 2.784l3.008 3.008 2.368-2.4-5.952-5.952 0.352-0.608c0.512-0.832 0.992-1.952 1.248-2.912 0.32-1.216 0.288-3.872-0.064-5.152-0.48-1.824-1.504-3.488-2.976-4.896-1.12-1.056-2.336-1.76-3.808-2.24-0.992-0.32-1.408-0.384-2.784-0.416-0.896-0.032-1.92 0-2.272 0.064M16.64 6.944c3.36 0.992 5.536 4.096 5.312 7.488-0.096 1.312-0.448 2.464-1.056 3.424-1.312 1.984-3.040 3.072-5.408 3.424-3.424 0.512-7.040-1.984-7.936-5.472-0.576-2.304 0.064-4.928 1.632-6.656 0.896-0.992 1.888-1.632 3.136-2.048 0.832-0.288 1.216-0.352 2.304-0.352 0.864 0 1.568 0.064 2.016 0.192"></path>
            </svg>
        );
    }

    private renderClearIcon() {
        return (
            <svg
                onClick={() => this.handleClearInput()}
                class="uic-search-field__icon uic-search-field__icon--interactable"
                viewBox="0 0 32 32"
                role="img"
                fill="currentColor"
            >
                <path d="M7.776 7.776l-1.152 1.152 7.392 7.392-7.456 7.424 1.12 1.12c0.64 0.64 1.184 1.12 1.248 1.12s1.792-1.632 3.776-3.648l3.68-3.648 7.456 7.456 2.208-2.4-7.36-7.36 7.36-7.392-2.368-2.368-7.36 7.36-7.392-7.36-1.152 1.152"></path>
            </svg>
        );
    }

    render() {
        const hasText = this.value !== "";

        return (
            <Host onClick={() => this.setFocus()}>
                <div class="uic-search-field__wrapper">
                    <input
                        type="text"
                        class="uic-search-field__input"
                        value={this.value}
                        disabled={this.disabled}
                        placeholder={this.placeholder}
                        onInput={(e) => this.handleInputChange(e)}
                        ref={(el) => (this.inputElement = el)}
                    />
                    {hasText ? this.renderClearIcon() : this.renderSearchIcon()}
                </div>
            </Host>
        );
    }
}
